import { AccountsReadmodelType } from '@bett3r-dev/teselly-domain';
/**
 * Navbar State
 *
 * Tracks:
 * - Mobile drawer open state
 * - Open menus and click-locked menus
 * - Account data from subscription
 * - App Login QR modal state
 */

/**
 * Navbar UI state
 */
export interface NavbarState {
  /** Mobile drawer open state */
  mobileDrawer: {
    isOpen: boolean;
  };

  /** Open dropdown menus (by key) */
  openMenus: Set<string>;

  /** Click-locked menus (prevent hover open after click) */
  clickLockedMenus: Set<string>;

  /** Account data from subscription */
  account: {
    name: string | null;
    data: Partial<AccountsReadmodelType> | null;
  };

  /** App Login QR modal state */
  appLoginQRModal: {
    isOpen: boolean;
    qrCodeImage: string | null;
    isLoading: boolean;
  };
}

/**
 * Initial state
 */
export const initialState: NavbarState = {
  mobileDrawer: { isOpen: false },
  openMenus: new Set(),
  clickLockedMenus: new Set(),
  account: {
    name: null,
    data: null
  },
  appLoginQRModal: {
    isOpen: false,
    qrCodeImage: null,
    isLoading: false
  }
};

/**
 * Selectors for querying navbar state
 */
export const selectors = {
  /**
   * Check if a menu is open (and not click-locked)
   */
  isMenuOpen: ( state: NavbarState, menuKey: string ): boolean => {
    return state.openMenus.has( menuKey ) && !state.clickLockedMenus.has( menuKey );
  },

  /**
   * Get account display name (fallback to 'Teselly')
   */
  accountDisplayName: ( state: NavbarState ): string => {
    return state.account.name || 'Teselly';
  },

  /**
   * Check if mobile drawer is open
   */
  isMobileDrawerOpen: ( state: NavbarState ): boolean => {
    return state.mobileDrawer.isOpen;
  },

  /**
   * Check if QR modal is open
   */
  isQRModalOpen: ( state: NavbarState ): boolean => {
    return state.appLoginQRModal.isOpen;
  },

  /**
   * Check if QR is loading
   */
  isQRLoading: ( state: NavbarState ): boolean => {
    return state.appLoginQRModal.isLoading;
  },

  /**
   * The account's Observation Mode status (ADR-068).
   *
   * Read straight off the account row the subscription already carries — there
   * is deliberately NO extra fetch and no second policy. `observationStatus` is
   * a field on `AccountsReadmodel`, and `accountSubscriptionPolicy` already
   * stores the whole row in `account.data` and keeps it live, so the indicator
   * updates the moment an `AccountEnteredObservation` / `AccountWentLive` event
   * projects.
   *
   * `undefined` is the normal reading for the overwhelming majority of accounts:
   * the field is never seeded at registration, only written by the
   * `AccountObservation` aggregate's events. An account that never entered
   * observation therefore has no value at all, and the domain resolves that to
   * `live` (`accountObservation.resolution.ts`). Callers must treat absent as
   * live — never as "unknown, show something".
   */
  observationStatus: ( state: NavbarState ): 'observing' | 'live' | undefined => {
    return state.account.data?.observationStatus;
  },

  /**
   * Whether the account is withholding outbound writes.
   *
   * The ONLY predicate the indicator should branch on. Note this is the
   * ACCOUNT-level default; the domain also models per-connector-kind overrides
   * (`connectorKindEgressModes`), so an account reading `live` here can still
   * have an individual connector kind observing, and vice versa. Resolving that
   * needs `resolveEgressMode` per kind — out of scope for the account-level
   * indicator, and the reason the popover is a popover.
   */
  isObserving: ( state: NavbarState ): boolean => {
    return state.account.data?.observationStatus === 'observing';
  }
};
